import { Day, Habit, State } from "../types";

export const calculateStreak = (habitId: number, currentDate: string, days: Day[], allowSkip: boolean) => {
  let streak = 0;
  let date = new Date(currentDate);
  let skippedThisWeek = false;

  const todayDone = days.some(day =>
    day.date === currentDate && day.habits.includes(habitId)
  );

  if (!todayDone) return 0;

  streak = 1;
  date.setDate(date.getDate() - 1);

  while (true) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const habitDone = days.some(day =>
      day.date === dateStr && day.habits.includes(habitId)
    );

    if (date.getDay() === 0) {
      skippedThisWeek = false;
    }

    if (habitDone) {
      streak++;
    } else if (allowSkip && !skippedThisWeek) {
      skippedThisWeek = true;
      streak++;
    } else {
      break;
    }

    date.setDate(date.getDate() - 1);
  }

  return streak;
};

export const hasWeeklyBonus = (streak: number) => {
  return streak >= 7;
};

export const calculateWeeklyStreak = (habitId: number, currentDate: string, days: Day[], allowSkip: boolean, isActive: boolean) => {
  const current = new Date(currentDate);
  const activeDays = [];
  
  // Check past 7 days (including today if active)
  for (let i = 0; i < 7; i++) {
    const checkDate = new Date(current);
    checkDate.setDate(current.getDate() - i);
    
    const year = checkDate.getFullYear();
    const month = String(checkDate.getMonth() + 1).padStart(2, '0');
    const day = String(checkDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // For today, check if active, otherwise check days array
    const isDayActive = dateStr === currentDate 
      ? isActive 
      : days.some(day => day.date === dateStr && day.habits.includes(habitId));
    
    if (isDayActive) {
      activeDays.push(dateStr);
    }
  }
  
  // If no activity in past 2 days (excluding today), show potential first day
  if (!isActive) {
    const yesterday = new Date(current);
    yesterday.setDate(current.getDate() - 1);
    const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
    
    const twoDaysAgo = new Date(current);
    twoDaysAgo.setDate(current.getDate() - 2);
    const twoDaysAgoStr = `${twoDaysAgo.getFullYear()}-${String(twoDaysAgo.getMonth() + 1).padStart(2, '0')}-${String(twoDaysAgo.getDate()).padStart(2, '0')}`;
    
    const yesterdayActive = days.some(day => day.date === yesterdayStr && day.habits.includes(habitId));
    const twoDaysAgoActive = days.some(day => day.date === twoDaysAgoStr && day.habits.includes(habitId));
    
    if (!yesterdayActive && !twoDaysAgoActive) {
      return {
        count: 1,
        isGrayedOut: true,
        isPotential: true
      };
    }
  }
  
  // Calculate actual streak count
  let streakCount = activeDays.length;
  
  // If not active today, show potential count
  if (!isActive) {
    streakCount = activeDays.length + 1;
  }
  
  return {
    count: streakCount,
    isGrayedOut: false,
    isPotential: !isActive
  };
};

export const getHabitProgress = (state: State, date: string, habitId: number) => {
  const day = state.days.find((day) => day.date === date);
  return day?.progress?.[habitId] || 0;
};

export const getLastUsageDate = (days: Day[], habitId: number): number => {
  const today = new Date();
  const lastUsedDay = days
    .filter(day => day.habits.includes(habitId))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

  if (!lastUsedDay) return Infinity;

  const lastUsedDate = new Date(lastUsedDay.date);
  const diffTime = today.getTime() - lastUsedDate.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export const getDayScore = (date: string, days: Day[]) => {
  const day = days.find((day) => day.date === date);
  return day ? day.score : 0;
};

export const getHabitColor = (score: number, colors: any) => {
  if (score > 0) return colors.green.darkest;
  if (score < 0) return colors.red.darkest;
  return "transparent";
};

export const getDayColor = (habits: any[], score: number, habitCount: number) => {
  const maxPositiveScore = habits.reduce(
    (total, habit) => total + (habit.score > 0 ? habit.score : 0),
    0
  );
  const maxNegativeScore = habits.reduce(
    (total, habit) => total + (habit.score < 0 ? habit.score : 0),
    0
  );

  if (score > 0) {
    if (score <= maxPositiveScore / 5) return "#c6f6d5";
    if (score <= (2 * maxPositiveScore) / 5) return "#9ae6b4";
    if (score <= (3 * maxPositiveScore) / 5) return "#68d391";
    if (score <= (4 * maxPositiveScore) / 5) return "#48bb78";
    return "#38a169";
  }
  if (score < 0) {
    if (score >= maxNegativeScore / 5) return "#fed7d7";
    if (score >= (2 * maxNegativeScore) / 5) return "#feb2b2";
    if (score >= (3 * maxNegativeScore) / 5) return "#fc8181";
    if (score >= (4 * maxNegativeScore) / 5) return "#f56565";
    return "#e53e3e";
  }
  return "transparent";
};

export const getSortedHabits = (habits: Habit[], sortOrder: "alphabetical" | "score", categoriesEnabled: boolean, categories?: any[]) => {
  return [...habits].sort((a, b) => {
    if (sortOrder === "alphabetical") {
      return a.name.localeCompare(b.name);
    } else if (sortOrder === "score") {
      return b.score - a.score;
    } else if (sortOrder === "categories" && categoriesEnabled) {
      if (!a.categoryId && b.categoryId) return -1;
      if (a.categoryId && !b.categoryId) return 1;
      if (a.categoryId && b.categoryId) {
        const categoryA = categories?.find(c => c.id === a.categoryId);
        const categoryB = categories?.find(c => c.id === b.categoryId);
        if (categoryA && categoryB) {
          return categoryA.name.localeCompare(categoryB.name);
        }
      }
      return a.name.localeCompare(b.name);
    }
    return 0;
  });
};